import { Fragment } from "react";
import logoLight from "@/assets/Logo-light.png";
import { HAZARD_LABELS, HORIZON_LABELS } from "@/lib/results";
import type { ReportModel } from "@/lib/report";
import type { ReportVariant } from "./ReportPage";
import { BASIS_TEXT } from "@/lib/report-basis";
import { cn } from "@/lib/utils";

/**
 * Dark cover panel (redesign). It fills the page's content area but is NOT
 * full-bleed in the exported PDF: pdf-generator.service.ts sets a 14mm
 * document-wide margin, so a white border shows around this panel. Chromium's
 * margin option can't be varied per page, so true full bleed would mean
 * margin:0 plus per-page CSS padding — a rewrite of the pagination strategy
 * ReportPage documents, not a cover tweak.
 */
export function ReportCover({ model, variant }: { model: ReportModel; variant: ReportVariant }) {
  // No year and no date, by design (scores aren't tied to an assessment year).
  const details: Array<[string, string]> = [
    ["Scenario", model.scenario ?? "Not applicable — current conditions only"],
    ["Time horizons", model.horizons.map((h) => HORIZON_LABELS[h]).join(", ")],
    ["Assets in scope", String(model.assets.length)],
    ["Hazards in scope", model.hazardsInScope.map((h) => HAZARD_LABELS[h]).join(", ")],
    ["Countries", model.countries.join(", ") || "—"],
    ["Basis", BASIS_TEXT.coverBasis],
    ["Filters applied", model.activeFilters.length ? model.activeFilters.join("; ") : "None"],
  ];

  return (
    <div
      className={cn(
        // Flex is safe here despite ReportPage's warning: the cover is one
        // page that never paginates. break-inside-avoid is insurance in case
        // a long "Filters applied" value pushes it past the page height.
        "flex break-inside-avoid flex-col justify-between gap-16 bg-brand-accent p-12 text-brand-white",
        // Explicit height, not min-h-full: the PDF variant's section has no
        // fixed height to resolve a percentage against, so the panel would
        // stop at its content and leave the rest of the page white. 265mm is
        // A4 height less the generator's 14mm top / 18mm bottom margins.
        variant === "pdf" ? "min-h-[265mm]" : "min-h-[297mm]",
      )}
    >
      <div className="space-y-1">
        <img src={logoLight} alt="180Pi" className="h-9 w-auto" />
        <p className="text-xs uppercase tracking-[0.08em] text-brand-slate-400">Climate data infrastructure</p>
      </div>

      <div className="space-y-4">
        <div className="h-0.5 w-12 bg-brand-slate-600" />
        {/* text-inherit is load-bearing: index.css's @layer base gives every
            h1-h6 `text-foreground` (near-black), and a direct rule beats the
            white this container merely passes down by inheritance. Without it
            the title renders black-on-dark-teal at 1.59:1. Any heading placed
            on a dark surface needs the same opt-out. */}
        <h1 className="max-w-[24ch] text-5xl font-normal leading-tight text-inherit">Climate risk assessment</h1>
        <p className="text-lg font-light tracking-wide text-brand-slate-400">{model.workspaceName}</p>
      </div>

      <dl className="grid grid-cols-2 gap-x-8 gap-y-4 border-t border-brand-white-20 pt-8 text-xs">
        {details.map(([term, value]) => (
          <Fragment key={term}>
            <div className="col-span-2 grid grid-cols-[10rem_1fr] gap-x-6">
              <dt className="uppercase tracking-[0.08em] text-brand-slate-400">{term}</dt>
              <dd className="font-medium text-brand-white-90">{value}</dd>
            </div>
          </Fragment>
        ))}
      </dl>
    </div>
  );
}
